import React from 'react';
import { motion } from 'framer-motion';
import {
    Shield,
    ArrowLeft,
    AlertCircle,
    RefreshCw,
    Ban,
    HeartHandshake,
    PowerOff,
    Copy,
    ThumbsUp,
    ThumbsDown,
    ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const PolicyCard = ({ title, content, example, footer, id, icon: Icon, delay }) => {
    const handleCopy = () => {
        navigator.clipboard.writeText(`${title}\n\n${content}\n\n${example}\n\n${footer}`);
        toast.success('Política copiada para a área de transferência');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay }}
            className="h-full"
        >
            <Card className="h-full bg-[#0a0f1c] border-blue-900/20 overflow-hidden flex flex-col group hover:border-blue-500/30 transition-all duration-300">
                <CardContent className="p-6 flex flex-col h-full relative">
                    {/* Header */}
                    <div className="flex flex-col items-center text-center mb-6">
                        <Badge variant="outline" className="mb-4 text-xs font-semibold tracking-wider border-blue-500/20 text-blue-400 bg-blue-500/5">
                            TERMOS E CONSENTIMENTO
                        </Badge>
                        <h3 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2 h-14 flex items-center justify-center">
                            {title}
                        </h3>
                    </div>

                    {/* Content */}
                    <div className="text-gray-400 text-sm leading-relaxed text-center mb-6 flex-grow">
                        {content}
                    </div>

                    {/* Example Box */}
                    <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4 mb-4 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-red-500/20" />
                        <p className="text-xs text-red-200/80 leading-relaxed">
                            {example}
                        </p>
                    </div>

                    {/* Footer Box */}
                    <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 mb-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-blue-500/20" />
                        <p className="text-xs text-blue-200/80 leading-relaxed">
                            {footer}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/5">
                        <div className="flex gap-2">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-white hover:bg-white/5">
                                <ThumbsUp className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-500 hover:text-white hover:bg-white/5">
                                <ThumbsDown className="h-4 w-4" />
                            </Button>
                        </div>

                        <Badge variant="secondary" className="bg-[#1a1f2e] text-purple-400 hover:bg-[#2a2f3e] border border-white/5 font-mono text-xs">
                            {id}
                        </Badge>

                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-white gap-2"
                            onClick={handleCopy}
                        >
                            <Copy className="h-3 w-3" />
                            Copiar
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

const ShoppingTerms = () => {
    const navigate = useNavigate();

    const policies = [
        {
            title: "Banimento e Doações",
            content: "Banimentos por quebra de regras não têm nenhuma relação com doações feitas pelos jogadores. Se você for banido por infringir as regras, não terá direito a reembolso dos itens ou benefícios que comprou. O pagamento por benefícios não exclui sua responsabilidade de seguir as regras.",
            example: "Um jogador que doou R$ 500 e comprou vários itens VIP comete várias infrações graves e é banido permanentemente. Ele pede reembolso alegando que pagou, mas não terá direito a nada de volta.",
            footer: "As doações são voluntárias e servem para apoiar o servidor, mas não dão imunidade ou privilégios em relação às regras. Todos devem seguir as mesmas normas.",
            id: "CONS-003",
            icon: Ban
        },
        {
            title: "Wipe e Perda de Benefícios",
            content: "Doações podem ter limites quando acontecerem atualizações, melhorias ou wipe (reset) no servidor com aviso prévio. Se você perder benefícios por causa disso, o servidor não precisa fazer reembolso, pois isso é um processo natural de todos os servidores.",
            example: "O servidor anuncia um wipe completo para melhorar a economia. Todos os itens, dinheiro e propriedades serão zerados. Jogadores que compraram carros VIP perderão tudo e não receberão reembolso, pois foi avisado previamente.",
            footer: "Wipes e resets são necessários para a saúde e equilíbrio do servidor. Os jogadores são avisados com antecedência e sabem que isso pode acontecer.",
            id: "CONS-004",
            icon: RefreshCw
        },
        {
            title: "Suspensão e Banimento sem Reembolso",
            content: "O servidor reserva-se o direito de suspender, banir ou encerrar o acesso de qualquer usuário a qualquer momento, por qualquer motivo, sem aviso prévio e sem obrigação de reembolso de qualquer valor pago por doações, itens VIP ou serviços premium. Banimentos por violação de regras não geram direito a compensação.",
            example: "Um jogador que doou R$ 1.000 e comprou vários itens VIP é banido por usar hack. Ele não tem direito a reembolso de nenhum valor, pois violou os termos.",
            footer: "Protege o servidor de obrigações de reembolso em casos de banimento, deixando claro que doações são voluntárias e não garantem imunidade.",
            id: "TU-007",
            icon: Shield
        },
        {
            title: "Natureza Voluntária das Doações",
            content: "Todas as doações feitas ao servidor são voluntárias, não reembolsáveis e não constituem compra de produtos ou serviços. Doações são contribuições para apoiar o servidor e podem conceder benefícios temporários, mas não criam obrigações contratuais ou direitos adquiridos permanentes.",
            example: "Quando você doa para o servidor, está apoiando voluntariamente o projeto. Os benefícios recebidos são cortesias, não produtos comprados. Se o servidor fechar ou você for banido, não há reembolso.",
            footer: "Estabelece que doações são contribuições voluntárias, não compras, evitando obrigações legais de reembolso e garantias de produto.",
            id: "TU-008",
            icon: HeartHandshake
        },
        {
            title: "Wipes e Resets",
            content: "O servidor reserva-se o direito de realizar wipes (resets completos), resets parciais, ajustes de economia, remoção de itens ou qualquer modificação nos dados do jogo a qualquer momento, com ou sem aviso prévio, sem obrigação de compensação ou reembolso aos usuários.",
            example: "A administração pode decidir fazer um wipe completo zerando tudo de todos os jogadores. Ninguém tem direito a reclamar reembolso ou compensação, pois isso está nos termos aceitos.",
            footer: "Protege o servidor do direito de fazer wipes e ajustes necessários sem criar obrigações de compensação aos jogadores afetados.",
            id: "TU-017",
            icon: RefreshCw
        },
        {
            title: "Encerramento do Serviço",
            content: "O servidor pode ser encerrado, descontinuado ou ter seus serviços interrompidos temporária ou permanentemente a qualquer momento, por qualquer motivo, sem aviso prévio e sem qualquer obrigação de reembolso, compensação ou transferência de dados aos usuários.",
            example: "Se os proprietários decidirem fechar o servidor permanentemente, todos perdem tudo e não têm direito a reembolso de doações ou compensação por tempo investido.",
            footer: "Protege o servidor do direito de encerrar operações sem criar obrigações financeiras ou legais com os usuários.",
            id: "TU-018",
            icon: PowerOff
        }
    ];

    return (
        <div className="min-h-screen bg-[#050505] text-gray-300 p-8 font-sans selection:bg-purple-500/30">
            <div className="max-w-7xl mx-auto">
                <Button
                    variant="ghost"
                    onClick={() => navigate('/')}
                    className="mb-8 hover:bg-white/5 text-gray-400 hover:text-white"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Voltar para o Login
                </Button>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden"
                >
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-10 border-b border-white/5 pb-8">
                        <div className="p-3 bg-purple-500/10 rounded-xl">
                            <ShoppingBag className="h-8 w-8 text-purple-500" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-white mb-1">Políticas de Compras</h1>
                            <p className="text-gray-500">Última atualização: {new Date().toLocaleDateString()}</p>
                        </div>
                    </div>

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {policies.map((policy, index) => (
                            <PolicyCard
                                key={policy.id}
                                {...policy}
                                delay={index * 0.1}
                            />
                        ))}
                    </div>

                    <div className="mt-12 pt-8 border-t border-white/5 text-center text-sm text-gray-500">
                        &copy; {new Date().getFullYear()} Grupo Capital. Todos os direitos reservados.
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default ShoppingTerms;
